import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const RootCanals = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Root Canal Treatment Fort Worth TX | Endodontic Therapy | Fort Worth Dental</title>
        <meta name="description" content="Gentle root canal treatment in Fort Worth TX. Save your natural tooth with expert endodontic therapy. Sedation available for comfort. Call (817) 985-3337." />
        <meta name="keywords" content="root canal fort worth, endodontic treatment fort worth tx, tooth infection treatment, root canal therapy, tooth pain relief" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/frame-1288-1.png)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Root Canal Therapy in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Save Your Tooth and Relieve Pain</p>
            <p className="text-sm mt-4 opacity-90">Painless Root Canal | Emergency Root Canal Treatment | Endodontic Therapy</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* Do I Want A Root Canal */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/root-canal-new-1.jpg" 
                  alt="Root Canal Treatment" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-1.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Do I Want A Root Canal?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  A dentist will always want to preserve your original, natural teeth if possible. A root canal is a tool of the dentist. It is sometimes his only option for saving your natural teeth as he also deals with the severe pain and the excess tooth decay.
                </p>
              </div>
            </div>
          </section>

          {/* When Do I Need A Root Canal */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">When Do I Need A Root Canal?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  When a cavity is undiagnosed and untreated for an extended period of time, the decay will eventually compromise the entire tooth. The decay will eventually even reach the bone structure that is supporting the tooth. The infection will certainly not heal on its own. It won't go away if you ignore it. Tooth decay that is ignored will negatively impact your entire body.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/root-canal-new-2.jpg" 
                  alt="When Root Canal Needed" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* What Is A Root Canal */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/root-canal-new-1.jpg" 
                  alt="Root Canal Procedure" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-1.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is A Root Canal?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  A dentist performs a root canal by using a series of files to eliminate all of the infected tissue from the tooth and the canal of the tooth. This will include removing the root of the tooth in the canal. After removing the decayed tissue, the dentist protects the tooth against infection in the future by filling the cavity. Once the decay is removed and the cavity is filled, the dentist will usually cover the entire tooth with a crown. This seals the tooth for the most complete protection.
                </p>
              </div>
            </div>
          </section>

          {/* What Symptoms Indicate The Need */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              What Symptoms Indicate The Need For A Root Canal?
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              You will be aware of the infection in your tooth as a result of several symptoms. Your infected tooth may reveal itself through extreme sensitivity to temperature. It may also be hyper sensitive to the sugar in sweets. You will very likely be experiencing severe pain. Other possible symptoms include swelling, severe pain when you bite down, or even a bad taste in your mouth.
            </p>
            <div className="bg-white p-6 rounded-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Common Symptoms:</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">•</span>
                  <span>Extreme sensitivity to hot or cold temperatures</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">•</span>
                  <span>Hypersensitivity to sugar in sweets</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">•</span>
                  <span>Severe, persistent toothache</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">•</span>
                  <span>Swelling around the affected tooth</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">•</span>
                  <span>Severe pain when biting down</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">•</span>
                  <span>Bad taste in your mouth</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Will A Root Canal Hurt */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Will A Root Canal Hurt?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Over the years, root canals have developed a bad reputation. However, the root canal is truly a positive tool available to the dentist. A root canal may keep you from needing dentures, or a bridge, or implants. Advances in technology and training have caused root canals to become fairly routine and relatively painless.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/root-canal-new-2.jpg" 
                  alt="Painless Root Canal" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="mb-16 bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Benefits of Root Canal Therapy</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Saves your natural tooth</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Relieves severe tooth pain</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Prevents spread of infection to surrounding teeth</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Maintains natural bite and chewing function</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>More affordable than extraction and replacement</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Modern procedures are virtually painless</span>
              </li>
            </ul>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Root Canal Therapy?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced team uses modern techniques and anesthesia to ensure a comfortable, pain-free root canal experience.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Pain-Free Treatment</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Root Canal Consultation</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental for emergency or scheduled root canal treatment in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Root Canal Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default RootCanals;